/**
 * Path constants for Firestore and Storage
 */

// Storage path structure
export const STORAGE_PATHS = {
  USERS: 'users',
  FAMILIES: 'families',
  VAULT: 'vault',
  DOCUMENTS: 'documents',
  PHOTOS: 'photos'
};

// Firestore path structure
export const FIRESTORE_PATHS = {
  USERS: 'users',
  FAMILIES: 'families',
  VAULT: 'vault'
};

// Collection types
export const COLLECTION_TYPES = {
  DOCUMENTS: 'documents',
  PHOTOS: 'photos',
  VIDEOS: 'videos',
  NOTES: 'notes',
  TASKS: 'tasks',
  EVENTS: 'events',
  CHAT: 'chatMessages',
  DISCUSSIONS: 'discussions',
  EMERGENCY: 'emergencyInfo',
  HEALTH: 'healthUpdates',
  RECOMMENDATIONS: 'recommendations',
  GOALS: 'goals',
  FUN: 'funActivities',
  FAMILY_TREE: 'familyTree',
  ANNOUNCEMENTS: 'announcements'
};

/**
 * Builds a storage path based on parameters
 * @param {Object} params
 * @param {string} params.userId - User ID
 * @param {string} params.familyCode - Family code (required for family/vault storage)
 * @param {string} params.collectionType - Type of collection (documents, photos, etc.)
 * @param {string} params.storageArea - Storage area (personal, family, vault)
 * @param {string} [params.fileId] - Optional file ID
 * @returns {string} Complete storage path
 */
export const buildStoragePath = ({ userId, familyCode, collectionType, storageArea, fileId = '' }) => {
  // Validate required parameters
  if (!userId) throw new Error('User ID is required');
  if (!collectionType) throw new Error('Collection type is required');
  if (!storageArea) throw new Error('Storage area is required');
  
  switch (storageArea.toLowerCase()) {
    case 'personal':
      return `${STORAGE_PATHS.USERS}/${userId}/${collectionType}${fileId ? `/${fileId}` : ''}`;
    case 'family':
      if (!familyCode) throw new Error('Family code is required for family storage');
      return `${STORAGE_PATHS.FAMILIES}/${familyCode}/${collectionType}${fileId ? `/${fileId}` : ''}`;
    case 'vault':
      if (!familyCode) throw new Error('Family code is required for vault storage');
      return `${STORAGE_PATHS.FAMILIES}/${familyCode}/${STORAGE_PATHS.VAULT}/${collectionType}${fileId ? `/${fileId}` : ''}`;
    default:
      throw new Error(`Invalid storage area: ${storageArea}`);
  }
};

/**
 * Builds a Firestore path based on parameters
 * @param {Object} params
 * @param {string} params.userId - User ID
 * @param {string} params.familyCode - Family code (required for family/vault storage)
 * @param {string} params.collectionType - Type of collection
 * @param {string} params.storageArea - Storage area (personal, family, vault)
 * @returns {string} Complete Firestore path
 */
export const buildFirestorePath = ({ userId, familyCode, collectionType, storageArea }) => {
  // Validate required parameters
  if (!userId) throw new Error('User ID is required');
  if (!collectionType) throw new Error('Collection type is required');
  if (!storageArea) throw new Error('Storage area is required');
  
  switch (storageArea.toLowerCase()) {
    case 'personal':
      return `${FIRESTORE_PATHS.USERS}/${userId}/${collectionType}`;
    case 'family':
      if (!familyCode) throw new Error('Family code is required for family storage');
      return `${FIRESTORE_PATHS.FAMILIES}/${familyCode}/${collectionType}`;
    case 'vault':
      if (!familyCode) throw new Error('Family code is required for vault storage');
      return `${FIRESTORE_PATHS.FAMILIES}/${familyCode}/${FIRESTORE_PATHS.VAULT}/${collectionType}`;
    default:
      throw new Error(`Invalid storage area: ${storageArea}`);
  }
}; 